/**
 * Ticket tags: the shared agent tag vocabulary plus one ticket's tags,
 * with add / remove mutations. Mirrors useTicketWatchers.ts.
 *
 * Wraps:
 *   GET    /api/v1/agent/tags
 *   GET    /api/v1/agent/tickets/{n}/tags
 *   POST   /api/v1/agent/tickets/{n}/tags
 *   DELETE /api/v1/agent/tickets/{n}/tags/{tag_id}
 *
 * Add / remove emit ticket_event rows server-side (tag_added /
 * tag_removed) so the audit trail tracks the change. Both mutations
 * invalidate the ticket's tag list, the global vocabulary (a created
 * tag joins the dropdown; a fully-detached one drops out - the
 * endpoint only returns in-use tags), and the agent list / count /
 * events caches, because a tag change moves tickets in and out of
 * tag-filtered inbox views.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type { TagAddRequest, TagResponse } from "@/types/api";

import { agentTicketCountsQueryKey } from "./useAgentTicketCounts";
import { ticketEventsQueryKeyPrefix } from "./useTicketEvents";

export const agentTagsQueryKey = ["agent", "tags"] as const;

/** All in-use tags, alphabetical by lower(name) server-side. Feeds the
 * inbox Tag filter dropdown and the TagsEditor autocomplete. */
export function useAgentTags(
  options: { enabled?: boolean } = {},
): UseQueryResult<TagResponse[]> {
  return useQuery({
    queryKey: agentTagsQueryKey,
    enabled: options.enabled !== false,
    // The vocabulary is tiny and changes rarely; 30s matches the inbox
    // polling heartbeat so the dropdown never lags the list for long.
    staleTime: 30_000,
    queryFn: async () => {
      const response = await api.get<TagResponse[]>("/agent/tags");
      return response.data;
    },
  });
}

export function ticketTagsQueryKey(
  ticketNumber: number,
): readonly unknown[] {
  return ["agent", "tickets", "tags", ticketNumber] as const;
}

export function useTicketTags(
  ticketNumber: number,
  options: { enabled?: boolean } = {},
): UseQueryResult<TagResponse[]> {
  return useQuery({
    queryKey: ticketTagsQueryKey(ticketNumber),
    enabled: options.enabled !== false,
    queryFn: async () => {
      const response = await api.get<TagResponse[]>(
        `/agent/tickets/${ticketNumber}/tags`,
      );
      return response.data;
    },
  });
}

/** Shared onSuccess invalidation for both tag mutations. Beyond the
 * two tag caches, this mirrors the list / counts / events keys
 * useUpdateTicket.ts invalidates (reusing its exported constants) and
 * adds the filter-aware count prefix: the ?tag= filter means an add /
 * remove can change which rows the list AND the subtitle count see. */
function invalidateAfterTagChange(
  queryClient: QueryClient,
  ticketNumber: number,
): void {
  void queryClient.invalidateQueries({
    queryKey: ticketTagsQueryKey(ticketNumber),
  });
  void queryClient.invalidateQueries({ queryKey: agentTagsQueryKey });
  void queryClient.invalidateQueries({
    queryKey: ["agent", "tickets", "list"],
  });
  void queryClient.invalidateQueries({
    queryKey: agentTicketCountsQueryKey,
  });
  void queryClient.invalidateQueries({
    queryKey: ["agent", "tickets", "filtered-count"],
  });
  void queryClient.invalidateQueries({
    queryKey: ticketEventsQueryKeyPrefix(ticketNumber),
  });
}

export function useAddTicketTag(
  ticketNumber: number,
): UseMutationResult<TagResponse, Error, TagAddRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: TagAddRequest) => {
      const response = await api.post<TagResponse>(
        `/agent/tickets/${ticketNumber}/tags`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateAfterTagChange(queryClient, ticketNumber);
    },
  });
}

export function useRemoveTicketTag(
  ticketNumber: number,
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tagId: string) => {
      await api.delete(`/agent/tickets/${ticketNumber}/tags/${tagId}`);
    },
    onSuccess: () => {
      invalidateAfterTagChange(queryClient, ticketNumber);
    },
  });
}
